import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBag, Minus, Plus, Save, Loader2, Package, FolderOpen, AlertCircle, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BackshopAbschriften = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // NEW: Location state
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  
  // Tab System: backshop oder tgtg
  const [abschriftTyp, setAbschriftTyp] = useState('backshop');
  
  // Confirmation Dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchData();
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const locs = response.data || [];
      setLocations(locs);
      
      // Set first location as default
      if (locs.length > 0) {
        setSelectedLocation(locs[0].id);
      } else {
        toast.error('Keine Standorte verfügbar');
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  const fetchData = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/backshop/categories`),
        axios.get(`${API}/backshop/products`, {
          params: { location_id: selectedLocation }
        })
      ]);
      
      setCategories(catRes.data || []);
      // Nur aktive Produkte anzeigen
      const activeProducts = (prodRes.data || []).filter(p => p.active !== false);
      setProducts(activeProducts);
      
      // Initialize quantities to 0 for active products only
      const initialQuantities = {};
      activeProducts.forEach(p => {
        initialQuantities[p.id] = 0;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleIncrement = (productId) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  };

  const handleDecrement = (productId) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(0, (prev[productId] || 0) - 1)
    }));
  };

  const handleIncrement5 = (productId) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 5
    }));
  };

  const getTotalItems = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getProductsWithQuantity = () => {
    return Object.entries(quantities).filter(([_, qty]) => qty > 0).length;
  };

  const handleSave = async () => {
    const items = [];
    
    Object.entries(quantities).forEach(([productId, quantity]) => {
      if (quantity > 0) {
        const product = products.find(p => p.id === productId);
        const category = categories.find(c => c.id === product?.category_id);
        
        if (product && category) {
          items.push({
            product_id: productId,
            product_name: product.name,
            article_number: product.article_number || null,
            category_id: category.id,
            category_name: category.name,
            quantity: quantity
          });
        }
      }
    });

    if (items.length === 0) {
      toast.error('Keine Artikel zum Speichern');
      return;
    }

    // Show confirmation dialog
    setPendingData({ items });
    setShowConfirmDialog(true);
  };

  const confirmSave = async () => {
    if (!pendingData) return;

    setSaving(true);
    setShowConfirmDialog(false);
    
    try {
      // Send with type field and location
      await axios.post(`${API}/backshop/writeoffs`, { 
        items: pendingData.items,
        type: abschriftTyp,  // 'backshop' or 'tgtg'
        location_id: selectedLocation
      });
      
      const typeName = abschriftTyp === 'tgtg' ? 'Too Good To Go' : 'Backshop';
      toast.success(`${pendingData.items.length} ${typeName} Abschriften gespeichert`);
      
      // Reset quantities
      const resetQuantities = {};
      products.forEach(p => {
        resetQuantities[p.id] = 0;
      });
      setQuantities(resetQuantities);
      setPendingData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getProductsByCategory = (categoryId) => {
    return products.filter(p => p.category_id === categoryId).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24 md:pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Backshop Abschriften
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Wählen Sie die Mengen mit + und - aus
            </p>
          </div>
          {getTotalItems() > 0 && (
            <Badge variant="secondary" className="text-base sm:text-lg px-3 sm:px-4 py-2">
              {getTotalItems()} Stück gesamt
            </Badge>
          )}
        </div>

        {/* Location Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-500" />
            <Label htmlFor="location-select" className="text-sm font-medium">
              Standort:
            </Label>
          </div>
          <Select
            value={selectedLocation}
            onValueChange={setSelectedLocation}
          >
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue placeholder="Standort auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tab System - Abschrift Type Selector */}
        <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-full sm:w-auto">
          <button
            onClick={() => setAbschriftTyp('backshop')}
            className={`flex-1 sm:flex-initial px-4 sm:px-6 py-2.5 sm:py-3 rounded-md font-medium text-sm sm:text-base transition-all ${
              abschriftTyp === 'backshop'
                ? 'bg-orange-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              Backshop Abschrift
            </span>
          </button>
          <button
            onClick={() => setAbschriftTyp('tgtg')}
            className={`flex-1 sm:flex-initial px-4 sm:px-6 py-2.5 sm:py-3 rounded-md font-medium text-sm sm:text-base transition-all ${
              abschriftTyp === 'tgtg'
                ? 'bg-green-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <Package className="h-4 w-4" />
              Too Good To Go
            </span>
          </button>
        </div>

        {/* Status Badge showing current type */}
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
            abschriftTyp === 'backshop' 
              ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300' 
              : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
          }`}>
            <span className={`h-2 w-2 rounded-full ${
              abschriftTyp === 'backshop' ? 'bg-orange-500' : 'bg-green-500'
            }`}></span>
            Aktuell: {abschriftTyp === 'tgtg' ? 'Too Good To Go' : 'Backshop Abschrift'}
          </div>
        </div>

        {!selectedLocation ? (
          <Card className="border-2 border-dashed">
            <CardContent className="py-16 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Kein Standort ausgewählt</p>
              <p className="text-muted-foreground mt-2">
                Bitte wählen Sie einen Standort aus, um Produkte zu laden.
              </p>
            </CardContent>
          </Card>
        ) : categories.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Keine Kategorien vorhanden</p>
              <p className="text-muted-foreground mt-2">
                Der Admin muss zuerst Kategorien und Produkte anlegen.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categories.map(category => {
              const categoryProducts = getProductsByCategory(category.id);
              if (categoryProducts.length === 0) return null;
              
              return (
                <Card key={category.id} className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2.5 sm:py-3">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5" />
                      {category.name}
                      <Badge variant="secondary" className="ml-auto bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs sm:text-sm">
                        {categoryProducts.length} Produkte
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {categoryProducts.map(product => (
                        <div 
                          key={product.id} 
                          className={`p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b last:border-b-0 ${
                            quantities[product.id] > 0 ? 'bg-orange-50 dark:bg-orange-950/40' : ''
                          }`}
                        >
                          {/* Mobile Layout - Kompakt & Elegant */}
                          <div className="flex items-center justify-between gap-3 md:hidden">
                            {/* Product Info - Links */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium text-sm truncate">{product.name}</span>
                                {product.article_number && (
                                  <span className="text-xs text-gray-500">#{product.article_number}</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Quantity Controls - Kompakt */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Minus Button - Kleiner */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDecrement(product.id)}
                                disabled={quantities[product.id] === 0}
                                className="h-10 w-10 p-0 text-red-600 hover:bg-red-50 hover:text-red-700 border-2 rounded-lg"
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              
                              {/* Quantity Display - Kompakt */}
                              <div className={`w-12 h-10 flex items-center justify-center rounded-lg border-2 font-semibold text-lg ${
                                quantities[product.id] > 0 
                                  ? abschriftTyp === 'tgtg'
                                    ? 'bg-green-100 dark:bg-green-900/50 border-green-400 text-green-700 dark:text-green-400'
                                    : 'bg-orange-100 dark:bg-orange-900/50 border-orange-400 text-orange-700 dark:text-orange-400'
                                  : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                              }`}>
                                {quantities[product.id] || 0}
                              </div>
                              
                              {/* Plus Button - Kleiner */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleIncrement(product.id)}
                                className="h-10 w-10 p-0 text-green-600 hover:bg-green-50 hover:text-green-700 border-2 rounded-lg"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Desktop Layout - Original */}
                          <div className="hidden md:flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Package className="h-5 w-5 text-gray-400" />
                              <div className="flex flex-col">
                                <span className="font-medium">{product.name}</span>
                                {product.article_number && (
                                  <span className="text-xs text-gray-500">#{product.article_number}</span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* -5 Button (optional) */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setQuantities(prev => ({
                                  ...prev,
                                  [product.id]: Math.max(0, (prev[product.id] || 0) - 5)
                                }))}
                                disabled={quantities[product.id] === 0}
                                className="h-10 w-10 p-0 text-gray-500"
                              >
                                -5
                              </Button>
                              
                              {/* Minus Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDecrement(product.id)}
                                disabled={quantities[product.id] === 0}
                                className="h-12 w-12 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                              >
                                <Minus className="h-5 w-5" />
                              </Button>
                              
                              {/* Quantity Display */}
                              <div className={`w-16 h-12 flex items-center justify-center rounded-md border-2 font-bold text-xl ${
                                quantities[product.id] > 0 
                                  ? abschriftTyp === 'tgtg'
                                    ? 'bg-green-100 dark:bg-green-900/50 border-green-400 text-green-700 dark:text-green-400'
                                    : 'bg-orange-100 dark:bg-orange-900/50 border-orange-400 text-orange-700 dark:text-orange-400'
                                  : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                              }`}>
                                {quantities[product.id] || 0}
                              </div>
                              
                              {/* Plus Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleIncrement(product.id)}
                                className="h-12 w-12 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                              >
                                <Plus className="h-5 w-5" />
                              </Button>
                              
                              {/* +5 Button (optional) */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleIncrement5(product.id)}
                                className="h-10 w-10 p-0 text-gray-500"
                              >
                                +5
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Save Button - Fixed at bottom on mobile */}
        {selectedLocation && categories.length > 0 && (
          <div className="fixed md:sticky bottom-0 left-0 right-0 md:bottom-4 p-4 md:p-0 bg-white dark:bg-gray-900 md:bg-transparent border-t md:border-t-0 shadow-lg md:shadow-none z-10">
            <Button
              onClick={handleSave}
              disabled={getTotalItems() === 0 || saving || !selectedLocation}
              className="w-full h-14 sm:h-16 text-base sm:text-lg font-semibold bg-orange-600 hover:bg-orange-700"
              size="lg"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Speichern ({getProductsWithQuantity()} Artikel, {getTotalItems()} Stück)
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className={`h-6 w-6 ${abschriftTyp === 'tgtg' ? 'text-green-600' : 'text-orange-600'}`} />
              Abschrift bestätigen
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-3">
              <p className="text-base text-gray-700 dark:text-gray-300">
                Du möchtest folgendes abschreiben:
              </p>
              
              <div className="space-y-2 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Typ:</span>
                  <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                    abschriftTyp === 'tgtg' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {abschriftTyp === 'tgtg' ? '🌱 Too Good To Go' : '🏪 Backshop Abschrift'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Artikel:</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {getProductsWithQuantity()} Produkte
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Gesamt:</span>
                  <span className="font-bold text-lg text-gray-900 dark:text-gray-100">
                    {getTotalItems()} Stück
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 pt-2">
                Bist du dir sicher, dass du diese Abschrift speichern möchtest?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="text-base">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmSave}
              className={`text-base ${
                abschriftTyp === 'tgtg' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              ✓ Ja, speichern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
